import { v4 } from 'uuid'
import { OutlineFactory } from 'web/engine/outline-factory'
import { timestampToSeconds } from "web/utils/time-utils"
import { assertDefined, getByName, lastItem, listToObject } from "web/utils/utils"
import YAML from 'yaml'
import { getBuildByVersion } from './core-data'
import { CLASS_ID, FIGHT_CATEGORY_ID } from './general/id'
import { BuildData, Fight, FightEvent, FightPlayer, FightSegment, FightSetup, SpellAssignment } from "./models"

export type YamlFightFormat = {
    fight: string
    segments: {
        name: string
        timestamp?: string

        // TODO: What even is this type? Just put the strings directly into the
        // array?
        events: {
            /**
             * Expected format: 00:10 | optional-event-name
             */
            event: string
        }[]
    }[]
}

type YamlSetupFormat = {
    players: {
        name: string
        specId: string
    }[]

    assignments: {
        playerName: string
        spellId: string

        /**
         * Expected to be a known and unique name
         */
        eventName: string
    }[]
}

export function loadMockNzothFight() {
    const yamlData = `
fight: "N'Zoth: The Tentacle Monster"
segments:
  - name: Phase 1
    events:
      - event: 00:25 | Torment 1 (split)
      - event: 00:50 | Torment 2
      - event: 01:00 | Portal 1 Starting
      - event: 01:15 | Torment 3 (Half)
      - event: 02:05 | Torment 4 (Half, Paranoia)
      - event: 03:28 | Torment 6 (Paranoia)
      - event: 03:28 | Torment 7
      - event: 04:10 | Portal 2 Starting
      - event: 04:45 | Torment 8 (Half)
      - event: 05:08 | Torment 9 (Half, Mindgrasp)

  - name: Phase 2
    timestamp: 06:00
    events:
      - event: 06:25 | Harvest 1
      - event: 06:29 | Anguish 1
      - event: 06:49 | Anguish 2
      - event: 07:23 | Anguish 3
      - event: 07:44 | Anguish 4 (Beam)

  - name: Secret Phase
    timestamp: 08:58
    events:
      - event: 09:08 | Bomb 1
      - event: 09:48 | Bomb 2
      - event: 10:15 | Bomb 3

  - name: Phase 3
    timestamp: 10:40
    events:
      - event: 10:45 | Harvest 2
      - event: 10:50 | Anguish 5
      - event: 11:10 | Anguish 6
      - event: 11:45 | Anguish 7
      - event: 12:06 | Anguish 8
    `
    const yamlObj = YAML.parse(yamlData) as YamlFightFormat
    return parseYamlFight(yamlObj)
}

export function loadSandboxMock() {
    const yamlData = `
fight: "Sandbox"
segments:
  - name: Phase 2
    timestamp: 00:30
    events:
        - event: 01:30 | Dmg
`
    const yamlObj = YAML.parse(yamlData) as YamlFightFormat
    return parseYamlFight(yamlObj)
}

export function loadMockNzothSetup(fight: YamlFightParts) {
    const build_1 = getBuildByVersion("1")
    const restoDruid = build_1.SpecByName(CLASS_ID.DRUID, "Restoration")

    const tranqSpellId = restoDruid.spellIds.filter(spellId => {
        return build_1.Spell(spellId).name === "Tranquility"
    })[0]

    assertDefined(tranqSpellId)

    return parseYamlFightSetup(
        fight,
        {
            players: [
                {
                    name: "Player 1",
                    specId: restoDruid.id,
                }
            ],

            assignments: [
                {
                    playerName: "Player 1",
                    spellId: tranqSpellId,
                    eventName: "Torment 2",
                }
            ]
        },
        build_1
    )
}

export type YamlFightParts = {
    fight: Fight
    segments: FightSegment[]
    events: FightEvent[]
}

// TODO: I itch to grab for this, clean it up and make it proper robust, integrate into
// the GUI
export function parseYamlFight(yamlFight: YamlFightFormat): YamlFightParts {

    const fight = OutlineFactory.createFight({
        fightName: yamlFight.fight,
        fightCategoryId: FIGHT_CATEGORY_ID.CUSTOM.Uncategorized
    })

    const fightSegments: FightSegment[] = []
    const fightEvents: FightEvent[] = []

    for (let index = 0; index < yamlFight.segments.length; index++) {
        const segment = yamlFight.segments[index]

        let fightSegment: FightSegment | undefined

        // Treating first entry as the default segment
        if (index === 0) {
            // Create default segment with 1 default event
            fightSegment = OutlineFactory.createSegment({
                fightId: fight.id,
                name: segment.name,
                userCreated: false,
                offset: 0,
            })

            fightSegments.push(fightSegment)

            const fightEvent = OutlineFactory.createEvent({
                fightSegmentId: fightSegment.id,
                name: "Pull",
                fightOffset: fightSegment.offset,
                userCreated: false,
                flags: []
            })

            fightEvents.push(fightEvent)
        }
        // Rest of entries are normal segments
        else {
            assertDefined(
                segment.timestamp,
                "Segments beyond the first one need an explicit timestamp"
            )

            // New segment
            fightSegment = OutlineFactory.createSegment({
                fightId: fight.id,
                name: segment.name,
                userCreated: true,
                offset: timestampToSeconds(segment.timestamp),
            })

            lastItem(fightSegments).childId = fightSegment.id
            fightSegments.push(fightSegment)
        }

        // Same for both branches
        segment.events.forEach(({ event }) => {
            const [timestamp, name] = event.split("|").map(i => i.trim())

            assertDefined(fightSegment)

            const fightEvent = OutlineFactory.createEvent({
                fightSegmentId: fightSegment.id,
                name: name,
                fightOffset: timestampToSeconds(timestamp),
                userCreated: true,
                flags: []
            })

            fightEvents.push(fightEvent)
        })
    }

    return {
        fight,
        segments: fightSegments,
        events: fightEvents
    }
}

export function parseYamlFightSetup(
    fightParts: YamlFightParts,
    yamlSetup: YamlSetupFormat,
    buildData: BuildData): FightSetup {
    const fightPlayers = yamlSetup.players.map<FightPlayer>(p => {
        return {
            id: v4(),
            name: p.name,
            specId: p.specId,
            talentIds: [],
            overrides: [],
            modifierIds: [],

            patchInfo: buildData.patchInfo
        }
    })

    // TODO: This is sketchy as hell, think I'm starting to abuse this function
    const fightPlayersByName = listToObject(fightPlayers.map(fp => ({
        id: fp.name,
        player: fp
    })))

    const spellAssignments = yamlSetup.assignments.map<SpellAssignment>(assgn => {
        return {
            playerId: fightPlayersByName[assgn.playerName].player.id,
            spellId: assgn.spellId,
            fightEventId: getByName(assgn.eventName, fightParts.events).id,
            eventOffset: 0,
        }
    })

    return {
        id: v4(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        fightId: fightParts.fight.id,
        name: "Untitled",

        patchInfo: buildData.patchInfo,

        fightPlayers,
        spellAssignments,
        teamAssignments: [],
        setupFightEvents: [],
    }
}
